import { Box, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import { Patient } from 'fhir/r4b';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormSelect } from 'src/components/form';
import { Row, Section } from 'src/components/layout';
import { ETHNICITY_OPTIONS, FormFields as AllFormFields, RACE_OPTIONS } from 'src/constants';
import { dataTestIds } from 'src/constants/data-test-ids';
import { LANGUAGE_OPTIONS, REQUIRED_FIELD_ERROR_MESSAGE } from 'utils';
import inPersonIntakeQuestionnaire from '../../../../../../../../config/oystehr/in-person-intake-questionnaire.json';

const FormFields = AllFormFields.patientDetails;
interface PatientDetailsContainerProps {
  patient: Patient | undefined;
  isLoading: boolean;
}
import { useTranslation } from 'react-i18next';

export const PatientDetailsContainer: FC<PatientDetailsContainerProps> = ({ isLoading }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { control } = useFormContext();

  const questionnaireLanguageOptions =
    (
      Object.values(inPersonIntakeQuestionnaire.fhirResources)[0]
        .resource.item.find((item) => item.linkId === 'patient-details-page')
        ?.item.find((item) => item.linkId === 'preferred-language') as any
    )?.answerOption?.map((option: any) => option.valueString) ?? [];

  const translatedEthnicityOptions = ETHNICITY_OPTIONS.map((option) => ({
    ...option,
    label: t(
      `patientProfile.options.ethnicity.${option.label.toLowerCase().includes('hispanic') ? (option.label.toLowerCase().startsWith('not') ? 'not_hispanic' : 'hispanic') : 'decline'}`,
      option.label
    ),
  }));

  const translatedRaceOptions = RACE_OPTIONS.map((option) => {
    let key = 'decline';
    if (option.label.includes('American Indian')) key = 'american_indian';
    else if (option.label === 'Asian') key = 'asian';
    else if (option.label.includes('Black')) key = 'black';
    else if (option.label.includes('Native Hawaiian')) key = 'native_hawaiian';
    else if (option.label === 'White') key = 'white';
    else if (option.label === 'Other') key = 'other';
    return { ...option, label: t(`patientProfile.options.race.${key}`, option.label) };
  });

  return (
    <Section title={t('patientProfile.sections.patientDetails')}>
      <Row
        label={t('patientProfile.fields.patientsEthnicity')}
        dataTestId={dataTestIds.patientDetailsContainer.patientsEthnicity}
        required
      >
        <FormSelect
          name={FormFields.ethnicity.key}
          control={control}
          disabled={isLoading}
          readOnly={true}
          options={translatedEthnicityOptions}
          rules={{
            required: REQUIRED_FIELD_ERROR_MESSAGE,
          }}
        />
      </Row>
      <Row
        label={t('patientProfile.fields.patientsRace')}
        dataTestId={dataTestIds.patientDetailsContainer.patientsRace}
        required
      >
        <FormSelect
          name={FormFields.race.key}
          control={control}
          disabled={isLoading}
          readOnly={true}
          options={translatedRaceOptions}
          rules={{
            required: REQUIRED_FIELD_ERROR_MESSAGE,
          }}
        />
      </Row>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '5px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: '0 1 30%' }}>
          <Typography sx={{ color: theme.palette.primary.dark }}>
            {t('patientProfile.fields.sendMarketingMessages')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 70%' }}>
          <Controller
            name={FormFields.sendMarketing.key}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={String(field.value) || ''}
                variant="standard"
                sx={{ width: '100%' }}
                disabled={isLoading}
                readOnly={true}
                onChange={(e) => {
                  const boolValue = e.target.value === 'true';
                  field.onChange(boolValue);
                }}
                data-testid={dataTestIds.patientDetailsContainer.sendMarketingMessages}
              >
                {[
                  { value: 'true', label: t('patientProfile.text.yes') },
                  { value: 'false', label: t('patientProfile.text.no') },
                ].map((option) => (
                  <MenuItem key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '5px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: '0 1 30%' }}>
          <Typography sx={{ color: theme.palette.primary.dark }}>
            {t('patientProfile.fields.preferredLanguage')}
          </Typography>
        </Box>
        <Stack sx={{ display: 'flex', alignItems: 'center', flex: '1 1 70%' }}>
          <Box width="100%">
            <Controller
              name={FormFields.language.key}
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value || ''}
                  variant="standard"
                  disabled={isLoading}
                  readOnly={true}
                  sx={{ width: '100%' }}
                  data-testid={dataTestIds.patientDetailsContainer.preferredLanguage}
                >
                  {Object.entries(LANGUAGE_OPTIONS)
                    .filter(([_key, value]) => questionnaireLanguageOptions.includes(value))
                    .map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {key === 'Spanish'
                          ? t('telemed.languages.es')
                          : key === 'English'
                            ? t('telemed.languages.en')
                            : key}
                      </MenuItem>
                    ))}
                </Select>
              )}
            />
          </Box>
        </Stack>
      </Box>
    </Section>
  );
};
